import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { useDebounceFn } from '@vueuse/core'
import { computed, onBeforeMount, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import { AddressForm } from '@/components/address/AddressAutofillForm.vue'

import { Address } from '@/interfaces/address'

interface Params {
	defaultAddress?: Address
	types?: string[]
}

export const waitForGoogleMaps = (timeoutMs = 10_000): Promise<void> =>
	new Promise((resolve, reject) => {
		const start = Date.now()
		const check = () => {
			if ((window as any).google?.maps?.importLibrary) {
				resolve()
				return
			}
			if (Date.now() - start > timeoutMs) {
				reject(new Error('Google Maps no cargó en el tiempo esperado'))
				return
			}
			setTimeout(check, 150)
		}
		check()
	})

const getComponent = (
	components: google.maps.GeocoderAddressComponent[],
	type: string,
	nameType: 'long_name' | 'short_name' = 'long_name'
) => components.find((c) => c.types.includes(type))?.[nameType] || ''

const useGoogleAddressAutofill = (params: Params) => {
	const { defaultAddress } = params
	const googleKey = import.meta.env.VITE_GOOGLE_KEY
	const toast = useToast()

	const showAllInputs = ref(defaultAddress !== undefined)
	const mapCenter = ref(
		defaultAddress?.latitude && defaultAddress?.longitude ?
			{
				lat: Number(defaultAddress.latitude),
				lng: Number(defaultAddress.longitude),
			}
		:	{ lat: 25.6866, lng: -100.3161 }
	) // Monterrey or defaultAddress
	const markerPosition = ref<{ lat: number; lng: number } | null>(
		defaultAddress?.latitude && defaultAddress?.longitude ?
			{
				lat: Number(defaultAddress.latitude),
				lng: Number(defaultAddress.longitude),
			}
		:	null
	)
	const activeView = ref<'roadmap' | 'satellite' | 'streetview'>('satellite')
	const streetViewAvailable = ref(false)
	const isUpdatingFromAutocomplete = ref(false)
	const isInitializing = ref(true)

	const addressForm = reactive<AddressForm>({
		street: defaultAddress?.street || '',
		country: defaultAddress?.country || '',
		state: defaultAddress?.state || '',
		city: defaultAddress?.city || '',
		neighborhood: defaultAddress?.neighborhood || '',
		zipcode: defaultAddress?.zipcode || '',
		number: defaultAddress?.number || '',
		internalNumber: defaultAddress?.internalNumber || '',
		latitude: Number(defaultAddress?.latitude) || 0,
		longitude: Number(defaultAddress?.longitude) || 0,
		countryCode: '',
		fullAddress: '',
		hotelName: '',
	})

	const searchValue = ref(
		defaultAddress ?
			[defaultAddress.street, defaultAddress.number, defaultAddress.city]
				.filter(Boolean)
				.join(', ')
		:	''
	)

	const addressVuelidate = useVuelidate<AddressForm>(
		{
			street: {
				required: helpers.withMessage('La calle es requerida', required),
			},
			country: {
				required: helpers.withMessage('El país es requerido', required),
			},
			state: {
				required: helpers.withMessage('El estado es requerido', required),
			},
			city: {
				required: helpers.withMessage('La ciudad es requerida', required),
			},
			zipcode: {
				required: helpers.withMessage(
					'El código postal es requerido',
					required
				),
			},
			number: {
				required: helpers.withMessage(
					'El número exterior es requerido',
					required
				),
			},
		},
		addressForm
	)

	const validForm = computed(() => !addressVuelidate.value.$invalid)

	const checkStreetView = async (lat: number, lng: number) => {
		try {
			const { StreetViewService, StreetViewStatus } = (await (
				window as any
			).google.maps.importLibrary('streetView')) as typeof google.maps
			const svc = new StreetViewService()
			svc.getPanorama(
				{ location: { lat, lng }, radius: 50 },
				(_data, status) => {
					streetViewAvailable.value = status === StreetViewStatus.OK
				}
			)
		} catch {
			streetViewAvailable.value = false
		}
	}

	const nearbyHotels = ref<{ lat: number; lng: number; name: string; address?: string }[]>([])

	const searchNearbyHotels = async (lat: number, lng: number) => {
		try {
			await waitForGoogleMaps()
			const { PlacesService } = (await (window as any).google.maps.importLibrary(
				'places'
			)) as typeof google.maps.places

			const div = document.createElement('div')
			const service = new PlacesService(div)

			service.nearbySearch(
				{
					location: { lat, lng },
					radius: 800, // 800 meters radius
					type: 'lodging',
				},
				(results, status) => {
					const { PlacesServiceStatus } = (window as any).google.maps.places
					if (status === PlacesServiceStatus.OK && results) {
						nearbyHotels.value = results
							.map((place) => {
								const location = place.geometry?.location
								if (!location) return null
								return {
									lat: location.lat(),
									lng: location.lng(),
									name: place.name || '',
									address: place.vicinity || '',
								}
							})
							.filter(Boolean) as { lat: number; lng: number; name: string; address: string }[]
					} else {
						nearbyHotels.value = []
					}
				}
			)
		} catch (error) {
			console.error('Error fetching nearby hotels:', error)
			nearbyHotels.value = []
		}
	}

	const geocodeAddress = useDebounceFn(async () => {
		if (isUpdatingFromAutocomplete.value || isInitializing.value) return

		const fullAddress = [
			addressForm.street,
			addressForm.number,
			addressForm.neighborhood,
			addressForm.city,
			addressForm.state,
			addressForm.country,
		]
			.filter(Boolean)
			.join(', ')

		if (!fullAddress || fullAddress.length < 5) return

		try {
			await waitForGoogleMaps()
			const { Geocoder, GeocoderStatus } = (await (
				window as any
			).google.maps.importLibrary('geocoding')) as typeof google.maps

			const geocoder = new Geocoder()

			geocoder.geocode(
				{ address: fullAddress },
				(
					results: google.maps.GeocoderResult[] | null,
					status: google.maps.GeocoderStatusString
				) => {
					if (status === GeocoderStatus.OK && results?.[0]) {
						const { location } = results[0].geometry
						const lat = location.lat()
						const lng = location.lng()

						mapCenter.value = { lat, lng }
						markerPosition.value = { lat, lng }
						addressForm.latitude = lat
						addressForm.longitude = lng
						addressForm.fullAddress = results[0].formatted_address
						checkStreetView(lat, lng)
						searchNearbyHotels(lat, lng)
					}
				}
			)
		} catch (error) {
			console.error('Geocoding error:', error)
		}
	}, 800)

	watch(
		() => [
			addressForm.street,
			addressForm.number,
			addressForm.city,
			addressForm.state,
			addressForm.country,
			addressForm.neighborhood,
			addressForm.zipcode,
		],
		() => {
			if (!isUpdatingFromAutocomplete.value && !isInitializing.value) {
				geocodeAddress()
			}
		}
	)

	const initAutocomplete = async (el: HTMLInputElement) => {
		try {
			const { Autocomplete } = (await (window as any).google.maps.importLibrary(
				'places'
			)) as typeof google.maps.places
			const autocomplete = new Autocomplete(el, {
				fields: ['address_components', 'formatted_address', 'geometry', 'name'],
				types: params.types,
			})

			autocomplete.addListener('place_changed', () => {
				const place = autocomplete.getPlace()
				if (!place.geometry?.location) {
					toast.error(
						'No se pudo obtener la dirección. Selecciona una opción del listado.'
					)
					return
				}

				isUpdatingFromAutocomplete.value = true

				const lat = place.geometry.location.lat()
				const lng = place.geometry.location.lng()
				const components = place.address_components ?? []

				mapCenter.value = { lat, lng }
				markerPosition.value = { lat, lng }
				showAllInputs.value = true
				activeView.value = 'satellite'

				addressForm.latitude = lat
				addressForm.longitude = lng
				addressForm.fullAddress = place.formatted_address ?? ''
				addressForm.hotelName = place.name ?? ''
				addressForm.number = getComponent(components, 'street_number')
				addressForm.street = getComponent(components, 'route')
				addressForm.neighborhood =
					getComponent(components, 'sublocality_level_1') ||
					getComponent(components, 'neighborhood')
				addressForm.city =
					getComponent(components, 'locality') ||
					getComponent(components, 'administrative_area_level_2')
				addressForm.state = getComponent(
					components,
					'administrative_area_level_1'
				)
				addressForm.country = getComponent(components, 'country')
				addressForm.countryCode = getComponent(
					components,
					'country',
					'short_name'
				).toLowerCase()
				addressForm.zipcode = getComponent(components, 'postal_code')

				searchValue.value = place.formatted_address ?? ''

				addressVuelidate.value.$touch()

				streetViewAvailable.value = false
				checkStreetView(lat, lng)
				searchNearbyHotels(lat, lng)

				setTimeout(() => {
					isUpdatingFromAutocomplete.value = false
				}, 1000)
			})
		} catch {
			toast.error(
				'No se pudo inicializar la búsqueda de direcciones. Intenta más tarde.'
			)
		}
	}

	const transformedForm = computed(() => ({ ...addressForm }))

	onMounted(async () => {
		if (defaultAddress?.latitude && defaultAddress?.longitude) {
			try {
				await waitForGoogleMaps()
				await checkStreetView(
					Number(defaultAddress.latitude),
					Number(defaultAddress.longitude)
				)
				await searchNearbyHotels(
					Number(defaultAddress.latitude),
					Number(defaultAddress.longitude)
				)
			} catch {
				streetViewAvailable.value = false
			}
		}

		// Enable geocoder synchronization after initialization
		setTimeout(() => {
			isInitializing.value = false
		}, 100)
	})

	return {
		googleKey,
		searchValue,
		addressForm,
		showAllInputs,
		mapCenter,
		markerPosition,
		activeView,
		streetViewAvailable,
		transformedForm,
		addressVuelidate,
		validForm,
		initAutocomplete,
		nearbyHotels,
		checkStreetView,
		searchNearbyHotels,
	}
}

export default useGoogleAddressAutofill
